/**
 * PS1 メモリーカード（複数枚）のキー・サーバー識別子。
 *
 * カード 0 は従来の `shared:psx` をそのまま使い、既存セーブを壊さない。
 * 追加カードは `shared:psx:<id>`。サーバー同期用 SHA-1 は固定表。
 */

export const SHARED_PSX_SAVE_KEY = 'shared:psx';
export const DEFAULT_PSX_MEMCARD_ID = '0';
export const MAX_PSX_MEMCARDS = 8;

/** サーバー同期用 SHA-1。シード文字列は旧称由来で固定（変更すると既存サーバーセーブと切れる）。 */
/** `sha1("retrocade:shared:psx")` — カード 0（既存） */
export const SHARED_PSX_SAVE_SHA1 = '0eb0c490f8776c4fe295a2b0c5eee1cc2eda5e2c';

/** `sha1("retrocade:shared:psx:<n>")` */
const PSX_MEMCARD_SHA1: Record<string, string> = {
  '0': SHARED_PSX_SAVE_SHA1,
  '1': '0dd5951dc4d5e12bd93b7c220e40ea4926ef540e',
  '2': 'ce974d27944fce7df9a043065b5780c19776dffa',
  '3': '400579d86df8233f6db5d0c8fb63b935870e6e7f',
  '4': 'f24c76c288dcb913bf47d46c06c49b33836f404d',
  '5': '9ace1cdd02865407ab519a99db44d6cef0d7cedd',
  '6': 'b3389366087f9d06c0c49556f738f4efdc034a34',
  '7': '00db6d642724b3a830545c53301e2cf94e9b11db',
};

export interface PsxMemcardMeta {
  id: string;
  name: string;
}

export const DEFAULT_PSX_MEMCARDS: PsxMemcardMeta[] = [{ id: DEFAULT_PSX_MEMCARD_ID, name: 'カード 1' }];

export function psxMemcardSaveKey(cardId: string): string {
  return cardId === DEFAULT_PSX_MEMCARD_ID ? SHARED_PSX_SAVE_KEY : `${SHARED_PSX_SAVE_KEY}:${cardId}`;
}

export function isPsxMemcardSaveKey(romId: string): boolean {
  return romId === SHARED_PSX_SAVE_KEY || romId.startsWith(`${SHARED_PSX_SAVE_KEY}:`);
}

export function psxMemcardIdFromSaveKey(romId: string): string | null {
  if (romId === SHARED_PSX_SAVE_KEY) return DEFAULT_PSX_MEMCARD_ID;
  const prefix = `${SHARED_PSX_SAVE_KEY}:`;
  if (!romId.startsWith(prefix)) return null;
  return romId.slice(prefix.length);
}

export function psxMemcardSha1(cardId: string): string | undefined {
  return PSX_MEMCARD_SHA1[cardId];
}

/** サーバー取り込み用: 全スロットの sha1 → saveKey */
export function allPsxMemcardBindings(): Array<{ cardId: string; saveKey: string; sha1: string }> {
  return Object.entries(PSX_MEMCARD_SHA1).map(([cardId, sha1]) => ({
    cardId,
    saveKey: psxMemcardSaveKey(cardId),
    sha1,
  }));
}

export function psxMemcardSha1FromSaveKey(romId: string): string | undefined {
  const id = psxMemcardIdFromSaveKey(romId);
  return id ? psxMemcardSha1(id) : undefined;
}

/** 次に追加するカード ID（未使用の最小番号） */
export function nextPsxMemcardId(existing: PsxMemcardMeta[]): string | null {
  const used = new Set(existing.map((c) => c.id));
  for (let i = 0; i < MAX_PSX_MEMCARDS; i++) {
    const id = String(i);
    if (!used.has(id) && PSX_MEMCARD_SHA1[id]) return id;
  }
  return null;
}

/**
 * PS1 メモリーカード容量（128 KiB = 1024 フレーム × 128 B）。
 *
 * 空カード画像は DuckStation の Format() に合わせる。
 * pcsx_rearmed の init_memcard はフレーム 63（書き込みテスト）をヘッダの
 * コピーにしないため、実機 BIOS でフォーマットしたカードと食い違う。
 * 最終電車など一部タイトルはそれを見てカード異常＝容量不足扱いになる。
 */
export const PSX_MEMCARD_BYTES = 128 * 1024;
const PSX_MEMCARD_FRAME = 128;
/** ブロック 0 末尾。実機フォーマットではヘッダと同じ内容が入る。 */
const PSX_MEMCARD_WRITE_TEST_FRAME = 63;

function psxMemcardFrameChecksum(frame: Uint8Array): number {
  let xor = 0;
  for (let i = 0; i < PSX_MEMCARD_FRAME - 1; i++) xor ^= frame[i] ?? 0;
  return xor & 0xff;
}

export function createBlankPsxMemcard(): Uint8Array {
  // DuckStation: 未使用データ領域は 0xFF で埋めてから TOC を書く
  const data = new Uint8Array(PSX_MEMCARD_BYTES);
  data.fill(0xff);

  const writeFrame = (frame: number, fill: number, edit: (f: Uint8Array) => void) => {
    const base = frame * PSX_MEMCARD_FRAME;
    data.fill(fill, base, base + PSX_MEMCARD_FRAME);
    const view = data.subarray(base, base + PSX_MEMCARD_FRAME);
    edit(view);
    view[PSX_MEMCARD_FRAME - 1] = psxMemcardFrameChecksum(view);
  };

  // ヘッダ
  writeFrame(0, 0, (f) => {
    f[0] = 0x4d; // 'M'
    f[1] = 0x43; // 'C'
  });

  // ディレクトリ（ブロック 1–15）
  for (let frame = 1; frame < 16; frame++) {
    writeFrame(frame, 0, (f) => {
      f[0] = 0xa0; // free
      f[8] = 0xff;
      f[9] = 0xff;
    });
  }

  // 壊れたセクタ一覧（フレーム 16–35）
  for (let frame = 16; frame < 36; frame++) {
    writeFrame(frame, 0, (f) => {
      f[0] = 0xff;
      f[1] = 0xff;
      f[2] = 0xff;
      f[3] = 0xff;
      f[8] = 0xff;
      f[9] = 0xff;
    });
  }

  // 壊れたセクタ置換・未使用フレームは 0
  data.fill(0, 36 * PSX_MEMCARD_FRAME, PSX_MEMCARD_WRITE_TEST_FRAME * PSX_MEMCARD_FRAME);

  // 書き込みテストフレーム = ヘッダのコピー（実機 BIOS / DuckStation と同じ）
  data.set(
    data.subarray(0, PSX_MEMCARD_FRAME),
    PSX_MEMCARD_WRITE_TEST_FRAME * PSX_MEMCARD_FRAME,
  );

  return data;
}

/** 先頭が "MC" ならフォーマット済みとみなす。 */
export function isFormattedPsxMemcard(data: Uint8Array): boolean {
  return data.length >= 2 && data[0] === 0x4d && data[1] === 0x43;
}

/**
 * 現行の空カード画像とバイト一致するか。
 * フラグ上は全部空きでも filesize／next 残骸、またはフレーム 63 欠落があると
 * ゲームが容量不足にする。
 */
export function isPristineBlankPsxMemcard(data: Uint8Array | null | undefined): boolean {
  if (!data || data.length !== PSX_MEMCARD_BYTES) return false;
  const blank = createBlankPsxMemcard();
  for (let i = 0; i < PSX_MEMCARD_BYTES; i++) {
    if (data[i] !== blank[i]) return false;
  }
  return true;
}

/**
 * 起動直前のカードを正規化する。
 *
 * - 未フォーマット／短すぎ → 空カード（DuckStation 互換・フレーム 63 付き）
 * - 有効セーブが 1 件も無い → 空カードへ作り直し（ゴースト filesize / 旧 pcsx 空画像対策）
 * - それ以外 → repair（filesize／孤児／コンパクト／書き込みテストフレーム）
 */
export function normalizePsxMemcardForBoot(
  data: Uint8Array | null | undefined,
): { data: Uint8Array; changed: boolean; reason: 'blank' | 'repair' | 'unchanged' } {
  if (!data || data.length === 0 || !isFormattedPsxMemcard(data)) {
    return { data: createBlankPsxMemcard(), changed: true, reason: 'blank' };
  }

  // セーブが無いカードは、フラグ上空きでも filesize 残骸でゲームが拒否しうる。
  // repair だけではデータ領域のゴミが残ることがあるので、丸ごと空カードにする。
  const repairedEarly = repairPsxMemcard(data);
  const forListing = repairedEarly?.data ?? data;
  if (listPsxMemcardSaves(forListing).length === 0) {
    if (isPristineBlankPsxMemcard(data)) {
      return { data, changed: false, reason: 'unchanged' };
    }
    return { data: createBlankPsxMemcard(), changed: true, reason: 'blank' };
  }

  if (repairedEarly?.changed) {
    return { data: repairedEarly.data, changed: true, reason: 'repair' };
  }
  return { data, changed: false, reason: 'unchanged' };
}

/** ディレクトリが扱うセーブ用ブロック数（ブロック 0 は TOC）。 */
export const PSX_MEMCARD_DATA_BLOCKS = 15;

export interface PsxMemcardUsage {
  /** "MC" ヘッダがあるか */
  formatted: boolean;
  totalBlocks: number;
  freeBlocks: number;
  usedBlocks: number;
}

/**
 * ディレクトリフレーム（ブロック 1–15）から空き／使用ブロックを数える。
 *
 * フラグ上位ニブル: 0xA0=空き（削除含む）、0x50=使用中。
 * データが無い・短すぎる場合は null（＝カード未挿入／未作成扱い）。
 */
export function inspectPsxMemcard(data: Uint8Array | null | undefined): PsxMemcardUsage | null {
  const dirEnd = 128 * (PSX_MEMCARD_DATA_BLOCKS + 1);
  if (!data || data.length < dirEnd) return null;
  if (!isFormattedPsxMemcard(data)) {
    return { formatted: false, totalBlocks: PSX_MEMCARD_DATA_BLOCKS, freeBlocks: 0, usedBlocks: 0 };
  }

  let freeBlocks = 0;
  let usedBlocks = 0;
  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    const flags = data[block * 128] ?? 0;
    const kind = flags & 0xf0;
    if (kind === 0xa0 || flags === 0x00) freeBlocks++;
    else if (kind === 0x50) usedBlocks++;
    else usedBlocks++; // 不明は「塞がっている」側に倒す
  }
  return {
    formatted: true,
    totalBlocks: PSX_MEMCARD_DATA_BLOCKS,
    freeBlocks,
    usedBlocks,
  };
}

/** 設定・HUD 用の短い説明。未接続は呼び出し側で別表示。 */
export function formatPsxMemcardUsage(usage: PsxMemcardUsage): string {
  if (!usage.formatted) return '未フォーマット';
  return `空き ${usage.freeBlocks}/${usage.totalBlocks}`;
}

const FRAME = 128;
const BLOCK = 8192;

export interface PsxMemcardSave {
  /** ディレクトリ上の先頭ブロック番号（1–15） */
  firstBlock: number;
  /** 占有ブロック数 */
  blocks: number;
  /** ディレクトリ上のファイル名（プロダクトコード＋スロット名など） */
  filename: string;
  /** タイトルフレームの Shift-JIS タイトル（取れなければ空） */
  title: string;
}

function readAscii(data: Uint8Array, offset: number, max: number): string {
  let end = offset;
  const limit = Math.min(offset + max, data.length);
  while (end < limit && data[end]) end++;
  return new TextDecoder('ascii', { fatal: false }).decode(data.subarray(offset, end)).trim();
}

function readShiftJisTitle(data: Uint8Array, block: number): string {
  const base = block * BLOCK + 4;
  if (base + 64 > data.length) return '';
  try {
    const raw = new TextDecoder('shift-jis', { fatal: false }).decode(data.subarray(base, base + 64));
    return raw.replace(/\0/g, '').trim();
  } catch {
    return '';
  }
}

function dirNextBlock(data: Uint8Array, block: number): number {
  // DuckStation / pcsx 互換: next はディレクトリフレーム offset +8（0 始まりブロック番号、FFFF=終端）
  const lo = data[block * FRAME + 8] ?? 0xff;
  const hi = data[block * FRAME + 9] ?? 0xff;
  return lo | (hi << 8);
}

function updateDirChecksum(data: Uint8Array, block: number): void {
  const base = block * FRAME;
  let xor = 0;
  for (let i = 0; i < FRAME - 1; i++) xor ^= data[base + i] ?? 0;
  data[base + FRAME - 1] = xor;
}

/** カード上のセーブ（先頭ブロック 0x51）を列挙する。 */
export function listPsxMemcardSaves(data: Uint8Array | null | undefined): PsxMemcardSave[] {
  if (!data || !isFormattedPsxMemcard(data) || data.length < PSX_MEMCARD_BYTES) return [];

  const saves: PsxMemcardSave[] = [];
  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    const flags = data[block * FRAME] ?? 0;
    if (flags !== 0x51) continue;

    const chain = collectSaveChain(data, block);
    const filename = readAscii(data, block * FRAME + 0x0a, 20);
    const title = readShiftJisTitle(data, block);
    saves.push({
      firstBlock: block,
      blocks: chain.length,
      filename: filename || `ブロック ${block}`,
      title: title || filename || `セーブ ${block}`,
    });
  }
  return saves;
}

/**
 * 指定セーブを削除して空きブロックにする（ディレクトリを 0xA0、データ領域も消去）。
 * 該当が無ければ null。
 */
export function deletePsxMemcardSave(
  data: Uint8Array,
  firstBlock: number,
): Uint8Array | null {
  if (!isFormattedPsxMemcard(data) || data.length < PSX_MEMCARD_BYTES) return null;
  if (firstBlock < 1 || firstBlock > PSX_MEMCARD_DATA_BLOCKS) return null;
  if ((data[firstBlock * FRAME] ?? 0) !== 0x51) return null;

  const out = new Uint8Array(data);
  let cur = firstBlock;
  for (let i = 0; i < PSX_MEMCARD_DATA_BLOCKS; i++) {
    const next = dirNextBlock(out, cur);
    markDirFree(out, cur);
    // データブロック消去
    out.fill(0, cur * BLOCK, cur * BLOCK + BLOCK);

    if (next >= PSX_MEMCARD_DATA_BLOCKS) break;
    const nextBlock = next + 1;
    if (nextBlock < 1 || nextBlock > PSX_MEMCARD_DATA_BLOCKS) break;
    cur = nextBlock;
  }
  return out;
}

function markDirFree(data: Uint8Array, block: number): void {
  data.fill(0, block * FRAME, block * FRAME + FRAME);
  data[block * FRAME] = 0xa0;
  data[block * FRAME + 8] = 0xff;
  data[block * FRAME + 9] = 0xff;
  updateDirChecksum(data, block);
}

function readDirFileSize(data: Uint8Array, block: number): number {
  const base = block * FRAME + 4;
  return (
    (data[base] ?? 0) |
    ((data[base + 1] ?? 0) << 8) |
    ((data[base + 2] ?? 0) << 16) |
    ((data[base + 3] ?? 0) << 24)
  ) >>> 0;
}

function writeDirFileSize(data: Uint8Array, block: number, size: number): void {
  const base = block * FRAME + 4;
  data[base] = size & 0xff;
  data[base + 1] = (size >> 8) & 0xff;
  data[base + 2] = (size >> 16) & 0xff;
  data[base + 3] = (size >> 24) & 0xff;
}

function writeDirNext(data: Uint8Array, block: number, nextIndex: number): void {
  data[block * FRAME + 8] = nextIndex & 0xff;
  data[block * FRAME + 9] = (nextIndex >> 8) & 0xff;
}

function collectSaveChain(data: Uint8Array, firstBlock: number): number[] {
  const chain: number[] = [firstBlock];
  let cur = firstBlock;
  for (let guard = 0; guard < PSX_MEMCARD_DATA_BLOCKS; guard++) {
    const next = dirNextBlock(data, cur);
    if (next >= PSX_MEMCARD_DATA_BLOCKS) break;
    const nextBlock = next + 1;
    if (nextBlock < 1 || nextBlock > PSX_MEMCARD_DATA_BLOCKS) break;
    if (chain.includes(nextBlock)) break;
    // 途中が空き／別セーブ先頭ならチェーンを切る（壊れた next の追随を止める）
    const flags = data[nextBlock * FRAME] ?? 0;
    if (flags === 0x51) break;
    if ((flags & 0xf0) === 0xa0 || flags === 0x00) break;
    chain.push(nextBlock);
    cur = nextBlock;
  }
  return chain;
}

export interface PsxMemcardRepairResult {
  data: Uint8Array;
  /** 何かしら直したか */
  changed: boolean;
  /** filesize がチェーン長と食い違っていた先頭ブロック数 */
  fixedFileSizes: number;
  /** どのセーブにも属さない 0x52/0x53 などを空きにした数 */
  freedOrphans: number;
  /** 削除済み 0xA1–A3 を新規空き 0xA0 に正規化した数 */
  normalizedDeleted: number;
  /** 書き込み途中の壊れたセーブを消した数 */
  deletedCorrupt: number;
  /** 空きを末尾に寄せるコンパクトを行ったか */
  compacted: boolean;
}

/**
 * ディレクトリが「書き込み途中」や矛盾状態なら false。
 * この状態を IndexedDB に落とすと、次回起動後もセーブ不能になる。
 */
export function isPsxMemcardConsistent(data: Uint8Array | null | undefined): boolean {
  if (!data || !isFormattedPsxMemcard(data) || data.length < PSX_MEMCARD_BYTES) return false;

  const claimed = new Set<number>();
  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    if ((data[block * FRAME] ?? 0) !== 0x51) continue;
    const chain = collectSaveChain(data, block);
    for (const b of chain) claimed.add(b);
    const lastNext = dirNextBlock(data, chain[chain.length - 1]!);
    // next が続くと言っているのに辿れない＝複数ブロック書き込みの途中
    if (lastNext < PSX_MEMCARD_DATA_BLOCKS) return false;
  }
  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    if (claimed.has(block)) continue;
    const flags = data[block * FRAME] ?? 0;
    if ((flags & 0xf0) === 0x50) return false;
  }
  return true;
}

/**
 * ゲームが「空き不足」と誤判定する典型原因を直す。
 *
 * - 複数ブロック書き込みの途中（next が空きを指す等）→ そのセーブを削除
 * - 先頭エントリの filesize が実チェーンと違う
 * - 0x52/0x53 の孤児
 * - 削除フラグ 0xA1–A3 の残骸
 * - 空きブロックを末尾へコンパクト（連続空きを必要とするソフト向け）
 */
export function repairPsxMemcard(data: Uint8Array): PsxMemcardRepairResult | null {
  if (!isFormattedPsxMemcard(data)) return null;

  const out = new Uint8Array(PSX_MEMCARD_BYTES);
  out.set(data.subarray(0, Math.min(data.length, PSX_MEMCARD_BYTES)));
  if (data.length < PSX_MEMCARD_BYTES) {
    const blank = createBlankPsxMemcard();
    out.set(blank.subarray(data.length), data.length);
  }

  let fixedFileSizes = 0;
  let freedOrphans = 0;
  let normalizedDeleted = 0;
  let deletedCorrupt = 0;
  let checksumFixes = 0;

  const claimed = new Set<number>();

  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    if ((out[block * FRAME] ?? 0) !== 0x51) continue;
    const chain = collectSaveChain(out, block);
    const lastNext = dirNextBlock(out, chain[chain.length - 1]!);
    // 書き込み途中: next が先を指すが辿れない（先が空き／別セーブ）
    if (lastNext < PSX_MEMCARD_DATA_BLOCKS) {
      for (const b of chain) {
        markDirFree(out, b);
        out.fill(0, b * BLOCK, b * BLOCK + BLOCK);
      }
      deletedCorrupt++;
      continue;
    }

    for (const b of chain) claimed.add(b);

    for (let i = 0; i < chain.length; i++) {
      const b = chain[i]!;
      const expectedFlag =
        chain.length === 1 ? 0x51 : i === 0 ? 0x51 : i === chain.length - 1 ? 0x53 : 0x52;
      out[b * FRAME] = expectedFlag;
      if (i < chain.length - 1) writeDirNext(out, b, chain[i + 1]! - 1);
      else writeDirNext(out, b, 0xffff);

      if (i === 0) {
        const expectedSize = chain.length * BLOCK;
        if (readDirFileSize(out, b) !== expectedSize) {
          writeDirFileSize(out, b, expectedSize);
          fixedFileSizes++;
        }
      } else {
        writeDirFileSize(out, b, 0);
        out.fill(0, b * FRAME + 0x0a, b * FRAME + 0x1f);
      }
    }
  }

  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    if (claimed.has(block)) continue;
    const flags = out[block * FRAME] ?? 0;
    const kind = flags & 0xf0;
    if (kind === 0x50) {
      markDirFree(out, block);
      out.fill(0, block * BLOCK, block * BLOCK + BLOCK);
      freedOrphans++;
      continue;
    }
    if (flags === 0xa1 || flags === 0xa2 || flags === 0xa3 || flags === 0x00) {
      markDirFree(out, block);
      normalizedDeleted++;
      continue;
    }
    if (kind === 0xa0) {
      const next = dirNextBlock(out, block);
      const dirty =
        next !== 0xffff ||
        readDirFileSize(out, block) !== 0 ||
        (out[block * FRAME + 1] ?? 0) !== 0 ||
        (out[block * FRAME + 2] ?? 0) !== 0 ||
        (out[block * FRAME + 3] ?? 0) !== 0;
      if (dirty || flags !== 0xa0) {
        markDirFree(out, block);
        normalizedDeleted++;
      }
    }
  }

  // 有効セーブを先頭から詰め、空きを末尾の連続領域にする
  const compacted = compactPsxMemcardInPlace(out);

  for (let block = 0; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    const base = block * FRAME;
    let xor = 0;
    for (let i = 0; i < FRAME - 1; i++) xor ^= out[base + i] ?? 0;
    if ((out[base + FRAME - 1] ?? 0) !== xor) {
      out[base + FRAME - 1] = xor;
      checksumFixes++;
    }
  }

  // 実機 BIOS / DuckStation: フレーム 63 はヘッダのコピー（書き込みテスト）
  let writeTestFixed = false;
  const writeTestBase = PSX_MEMCARD_WRITE_TEST_FRAME * FRAME;
  for (let i = 0; i < FRAME; i++) {
    if (out[writeTestBase + i] !== out[i]) {
      writeTestFixed = true;
      break;
    }
  }
  if (writeTestFixed) {
    out.set(out.subarray(0, FRAME), writeTestBase);
  }

  const changed =
    fixedFileSizes > 0 ||
    freedOrphans > 0 ||
    normalizedDeleted > 0 ||
    deletedCorrupt > 0 ||
    compacted ||
    checksumFixes > 0 ||
    writeTestFixed ||
    data.length !== PSX_MEMCARD_BYTES;

  return {
    data: out,
    changed,
    fixedFileSizes,
    freedOrphans,
    normalizedDeleted,
    deletedCorrupt,
    compacted,
  };
}

/**
 * 有効な 0x51 セーブをブロック 1 から詰めて配置し直す。
 * 連続空きを要求するソフト向け。中身は維持する。
 */
function compactPsxMemcardInPlace(data: Uint8Array): boolean {
  type SavePack = {
    chain: number[];
    dirFrames: Uint8Array[];
    dataBlocks: Uint8Array[];
  };
  const packs: SavePack[] = [];
  const visited = new Set<number>();

  for (let block = 1; block <= PSX_MEMCARD_DATA_BLOCKS; block++) {
    if ((data[block * FRAME] ?? 0) !== 0x51) continue;
    if (visited.has(block)) continue;
    const chain = collectSaveChain(data, block);
    for (const b of chain) visited.add(b);
    packs.push({
      chain,
      dirFrames: chain.map((b) => Uint8Array.from(data.subarray(b * FRAME, b * FRAME + FRAME))),
      dataBlocks: chain.map((b) => Uint8Array.from(data.subarray(b * BLOCK, b * BLOCK + BLOCK))),
    });
  }

  if (packs.length === 0) return false;

  let cursor = 1;
  let alreadyPacked = true;
  for (const pack of packs) {
    for (let i = 0; i < pack.chain.length; i++) {
      if (pack.chain[i] !== cursor + i) {
        alreadyPacked = false;
        break;
      }
    }
    if (!alreadyPacked) break;
    cursor += pack.chain.length;
  }
  if (alreadyPacked) return false;

  const blank = createBlankPsxMemcard();
  data.set(blank);

  let dest = 1;
  for (const pack of packs) {
    const n = pack.dirFrames.length;
    for (let i = 0; i < n; i++) {
      const b = dest + i;
      data.set(pack.dirFrames[i]!, b * FRAME);
      data.set(pack.dataBlocks[i]!, b * BLOCK);
      const flag = n === 1 ? 0x51 : i === 0 ? 0x51 : i === n - 1 ? 0x53 : 0x52;
      data[b * FRAME] = flag;
      if (i < n - 1) writeDirNext(data, b, b);
      else writeDirNext(data, b, 0xffff);
      if (i === 0) writeDirFileSize(data, b, n * BLOCK);
      else {
        writeDirFileSize(data, b, 0);
        data.fill(0, b * FRAME + 0x0a, b * FRAME + 0x1f);
      }
      updateDirChecksum(data, b);
    }
    dest += n;
  }
  return true;
}
